/**
 * companies コレクションの購読・作成・更新。
 * #2133 設計 §4: 会社(admin) 一覧・作成・編集。書き込み先は companies/{id}。
 * packages/common CompanyUsecases.create/update と同じく、ドキュメント全体を
 * set()/update() する(admin 専用の操作であり、Flutter と同じくフィールドの
 * 絞り込みは行わない)。
 */
import { collection, doc, getDoc, onSnapshot, setDoc, updateDoc, type Unsubscribe } from 'firebase/firestore';
import { db } from './client';
import { logger } from '../logger';
import type { Company } from '@/types/firestore';

const COLLECTION = 'companies';

export function subscribeToCompanies(
  onChange: (companies: Company[]) => void,
  onError: (error: unknown) => void
): Unsubscribe {
  if (!db) {
    logger.error('Firestore not initialized');
    onError(new Error('Firestore not initialized'));
    return () => {};
  }

  return onSnapshot(
    collection(db, COLLECTION),
    (snapshot) => {
      onChange(snapshot.docs.map((d) => d.data() as Company));
    },
    (error) => {
      logger.error('Failed to subscribe to companies', error);
      onError(error);
    }
  );
}

export async function fetchCompany(companyId: string): Promise<Company | null> {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  const snapshot = await getDoc(doc(db, COLLECTION, companyId));
  return snapshot.exists() ? (snapshot.data() as Company) : null;
}

export async function createCompany(company: Company): Promise<void> {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  await setDoc(doc(db, COLLECTION, company.id), company);
}

export async function updateCompany(company: Company): Promise<void> {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  await updateDoc(doc(db, COLLECTION, company.id), { ...company } as Record<string, unknown>);
}
